// The month of the calendar view (R-E10): weeks from Monday, the records of each week in lanes — a record over several days is one bar
// across its days, cut at the edges of the week — and what does not fit a day's lanes counted as «ещё N».

/** A record on the calendar: its first and last day (ГГГГ-ММ-ДД, both included). */
export interface CalItem { id: string; start: string; end: string }
/** A bar of one week: the record, its first and last column (0 — Monday) and whether it goes on before or after the week. */
export interface CalBar { id: string; lane: number; from: number; to: number; before: boolean; after: boolean }
export interface CalWeek { days: string[]; bars: CalBar[]; /** Records of each day that did not fit the lanes. */ more: string[][] }

const DAY_MS = 86_400_000;
export const isoDay = (t: number) => new Date(t).toISOString().slice(0, 10);
const at = (day: string) => Date.parse(`${day}T00:00:00Z`);
export const addDay = (day: string, n: number) => isoDay(at(day) + n * DAY_MS);

/** The first day of the month of `day` and of the month `n` months away. */
export function monthOf(day: string, n = 0): string {
  const d = new Date(at(day.slice(0, 7) + '-01'));
  d.setUTCMonth(d.getUTCMonth() + n);
  return isoDay(d.getTime());
}

/** «Ноябрь 2026». */
export function monthLabel(first: string): string {
  const s = new Date(at(first)).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric', timeZone: 'UTC' }).replace(/\s*г\.$/, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The days of the grid: whole weeks from the Monday on or before the 1st to the Sunday on or after the last day of the month. */
export function gridDays(first: string): string[][] {
  const lead = (new Date(at(first)).getUTCDay() + 6) % 7;
  const last = addDay(monthOf(first, 1), -1);
  const weeks: string[][] = [];
  for (let w = addDay(first, -lead); w <= last; w = addDay(w, 7)) weeks.push(Array.from({ length: 7 }, (_, i) => addDay(w, i)));
  return weeks;
}

/**
 * Lays the records out on the weeks: longer records first, then by start, each in the lowest lane free on all its days of the week;
 * a record that finds no lane below `lanes` is counted in `more` of each of its days.
 */
export function layMonth(first: string, items: CalItem[], lanes: number): CalWeek[] {
  const sorted = [...items].sort((a, b) => (at(b.end) - at(b.start)) - (at(a.end) - at(a.start)) || a.start.localeCompare(b.start) || a.id.localeCompare(b.id));
  return gridDays(first).map((days) => {
    const ws = days[0]!, we = days[6]!;
    const used: boolean[][] = Array.from({ length: lanes }, () => Array(7).fill(false) as boolean[]);
    const bars: CalBar[] = [];
    const more: string[][] = days.map(() => []);
    for (const it of sorted) {
      if (it.end < ws || it.start > we) continue;
      const from = Math.max(0, Math.round((at(it.start) - at(ws)) / DAY_MS)), to = Math.min(6, Math.round((at(it.end) - at(ws)) / DAY_MS));
      const lane = used.findIndex((row) => row.slice(from, to + 1).every((x) => !x));
      if (lane < 0) { for (let c = from; c <= to; c++) more[c]!.push(it.id); continue; }
      for (let c = from; c <= to; c++) used[lane]![c] = true;
      bars.push({ id: it.id, lane, from, to, before: it.start < ws, after: it.end > we });
    }
    return { days, bars, more };
  });
}

/** A record's days from its fields: a missing or earlier end is its start; a datetime counts by its day. */
export function itemOf(id: string, start: unknown, end: unknown): CalItem | null {
  const day = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null);
  const s = day(start);
  if (!s) return null;
  const e = day(end);
  return { id, start: s, end: e && e >= s ? e : s };
}
